import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Empty } from 'antd';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { ModelLead } from '@app/api/Leads/types';
import { useLeadCardContext } from '@app/components/LeadCard/context';
import { LeadListItem } from '@app/components/LeadCard/ui/LeadListItem';
import { LeadListSkeleton } from '@app/components/LeadCard/ui/LeadListSkeleton';
import { useClientsAccessRights } from '@app/hooks';
import { LEADS_SCROLL_LOAD_THRESHOLD_PX } from '@app/pages/Leads/LeadsRegistryPage/model/constants';
import { useScrollableContent } from '@app/shared/hooks';

import type { FC, UIEvent } from 'react';

import './LeadListPanel.scss';

interface LeadListPanelProps {
  closeDrawer?(): void;
}

const NEXT_PAGE_SKELETON_ROWS_COUNT = 3;

export const LeadListPanel: FC<LeadListPanelProps> = ({ closeDrawer }) => {
  const {
    leads,
    hasNextLeadsPage,
    onBackToLeads,
    onLoadNextLeads,
    selectedLeadId,
    selectLead,
    isLeadsLoading,
    isNextLeadsPageLoading,
  } = useLeadCardContext();
  const { hasUserRightToShowContacts } = useClientsAccessRights();

  const hasLeads = useMemo(() => leads.length > 0, [leads]);
  const isLoadMoreRequestedRef = useRef(false);
  const { hasScrollableContent, scrollContainerRef } =
    useScrollableContent<HTMLDivElement>(
      `${leads.length}:${hasNextLeadsPage}:${isNextLeadsPageLoading}:${isLeadsLoading}`
    );
  const shouldShowLoadMoreButton = hasNextLeadsPage && !hasScrollableContent;

  const leadClickHandler = useCallback(
    (lead: ModelLead) => {
      if (isLeadsLoading) return;

      selectLead(lead);
      closeDrawer?.();
    },
    [isLeadsLoading, selectLead, closeDrawer]
  );

  const loadNextLeadsHandler = useCallback(() => {
    if (isNextLeadsPageLoading || isLoadMoreRequestedRef.current) return;

    isLoadMoreRequestedRef.current = true;
    onLoadNextLeads?.();
  }, [isNextLeadsPageLoading, onLoadNextLeads]);

  const scrollHandler = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (
        isLeadsLoading ||
        isNextLeadsPageLoading ||
        isLoadMoreRequestedRef.current ||
        !hasNextLeadsPage ||
        !hasScrollableContent
      ) {
        return;
      }

      const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      if (distanceToBottom <= LEADS_SCROLL_LOAD_THRESHOLD_PX) {
        isLoadMoreRequestedRef.current = true;
        onLoadNextLeads?.();
      }
    },
    [
      hasNextLeadsPage,
      hasScrollableContent,
      isLeadsLoading,
      isNextLeadsPageLoading,
      onLoadNextLeads,
    ]
  );

  useEffect(() => {
    if (!isNextLeadsPageLoading) {
      isLoadMoreRequestedRef.current = false;
    }
  }, [isNextLeadsPageLoading, leads.length]);

  return (
    <section className="LeadListPanel">
      <div className="LeadListPanel__top">
        <Button
          icon={<ArrowLeftOutlined />}
          block
          disabled={isLeadsLoading}
          onClick={onBackToLeads}
          size="small"
        >
          Назад к лидам
        </Button>
      </div>

      <div
        className="LeadListPanel__list"
        ref={scrollContainerRef}
        onScroll={scrollHandler}
      >
        {isLeadsLoading ? (
          <LeadListSkeleton />
        ) : hasLeads ? (
          <>
            {leads.map((lead) => (
              <LeadListItem
                lead={lead}
                key={lead.Id}
                canShowContacts={hasUserRightToShowContacts}
                isSelected={lead.Id === selectedLeadId}
                onClick={leadClickHandler}
              />
            ))}

            {isNextLeadsPageLoading && (
              <LeadListSkeleton rowsCount={NEXT_PAGE_SKELETON_ROWS_COUNT} />
            )}

            {shouldShowLoadMoreButton && (
              <Button
                className="LeadListPanel__show-next"
                block
                disabled={isNextLeadsPageLoading}
                loading={isNextLeadsPageLoading}
                onClick={loadNextLeadsHandler}
              >
                Показать следующие
              </Button>
            )}
          </>
        ) : (
          <Empty
            className="LeadListPanel__empty"
            description="Лиды не найдены"
          />
        )}
      </div>
    </section>
  );
};
